import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  CircularProgress,
  Snackbar,
  ThemeProvider,
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch, useSelector } from "react-redux";
import {
  createLogEntryPayload,
  Trackable,
  withTrackProps,
} from "@dpdgroupuk/react-event-tracker";

import makeStyles from "@material-ui/core/styles/makeStyles";
import {
  fetchVehicles,
  fetchDepotDrivers,
  changePassword,
  updateDriver,
  assignRouteDriver,
} from "../../services/driver";
import {
  getDriverByCode,
  getRouteOdfDriver,
  isEmptyOdfDriver,
  isRouteDriverChanged,
  mapDriverForFormModel,
} from "./model";
import { DRIVER_TYPES, EMPTY_DRIVER, EMPTY_ODF_DRIVER } from "./constants";
import ConfirmSnackbar from "../../components/modals/ConfirmSnackbar";
import ModalControlled from "../../components/ModalControlled";
import {
  ARE_YOU_WANT_TO_DISCARD,
  NEW_DRIVER_CREATED,
  PROCEED_WITH_PASSWORD_CHANGES,
  SOMETHING_WENT_WRONG,
  UNABLE_TO_RESET_PASSWORD,
  YOU_HAVE_UNSAVED_CHANGES,
  ERROR_$,
} from "./messages";
import theme from "../../components/styles/driverDetails";
import { wrapPromise } from "../../utils/promise";
import palette from "../../components/styles/palette";
import useLocationQueryParams from "../../hooks/useLocationQueryParams";
import { DRIVER_DETAILS } from "../../constants/strings";
import { fetchRouteDetail } from "../../store/actions/route";
import { View } from "../../components/Base";
import { DRIVER_DETAILS as DRIVER_DETAILS_IDS } from "../../constants/analytics";
import useFetchWithCondition from "../../hooks/useFetchWithCondition";
import { getCurrentUserId } from "../../store/selectors/user";
import { getSectionList } from "../../services/section";
import useLoader from "../../hooks/useLoader";
import {
  CHANGE_PASSWORD_PROCESS,
  SUBMIT_DRIVER_PROCESS,
} from "../../enums/loaderProcess";
import CreateDriver from "./CreateDriver";
import DriverDetails from "./DriverDetailsDialog";
import { fetchRoutes } from "../../store/actions/routes";

const useStyles = makeStyles(({ typography }) => ({
  header: {
    backgroundColor: "#E6E7E8",
    height: "1.5rem",
    display: "flex",
    padding: "1rem 1.25rem 0.5rem",
    justifyContent: "space-between",
  },
  headerText: {
    color: palette.typography.black.high,
    textTransform: "uppercase",
    fontSize: typography.pxToRem(18),
    fontWeight: 500,
    fontFamily: "Roboto",
    lineHeight: typography.pxToRem(24),
  },
  close: {
    cursor: "pointer",
  },
  icon: {
    fill: palette.typography.black.solid,
  },
  container: {
    padding: typography.pxToRem(20),
    width: "35rem",
    display: "flex",
    justifyContent: "center",
  },
}));

const DriverDetailsDialog = ({
  open,
  onClose,
  route = {},
  handleSave,
  onSelectDriver,
  handleCloseModal,
  onChangePassword,
  onAddDriver,
  cancelChangePassword,
  confirmChangePassword,
  cancelUnsavedChanges,
  confirmUnsavedChanges,
}) => {
  const styles = useStyles();
  const form = useRef();
  const changePassModalRef = useRef();
  const saveChangesModalRef = useRef();
  const dispatch = useDispatch();
  const {
    depot: depotCode,
    date,
    routeId,
    detailVisible,
    sectionId,
  } = useLocationQueryParams();
  const isRouteDetail = !!(routeId && detailVisible);
  const [message, setMessage] = useState("");
  const [isCreateDriverFlow, setCreateDriverFlow] = useState(false);
  const [driver, setActiveDriver] = useState(EMPTY_DRIVER);
  const [odfDriver, setActiveOdfDriver] = useState(EMPTY_ODF_DRIVER);
  const uid = useSelector(getCurrentUserId);
  const [showLoader, hideLoader] = useLoader();

  const { data: vehicles, error: vehiclesError } = useFetchWithCondition(
    "vehicles",
    fetchVehicles,
    open
  );

  const { data: drivers, error: driversError } = useFetchWithCondition(
    "drivers",
    async () => {
      const drivers = (await fetchDepotDrivers(depotCode, date)).map(
        mapDriverForFormModel
      );
      return drivers;
    },
    open
  );

  const { data: sections, error: sectionsError } = useFetchWithCondition(
    "sections",
    () => getSectionList(depotCode, date, uid),
    open
  );

  const showMessage = useCallback((msgText) => {
    setMessage(msgText);
  }, []);

  const handleClose = useCallback(async () => {
    handleCloseModal();
    if (form?.current?.dirty) {
      const [, error] = await wrapPromise(
        saveChangesModalRef.current.showModal()
      );
      if (error) return;
    }
    onClose();
  }, [onClose, handleCloseModal, saveChangesModalRef]);

  const handleChangePassword = useCallback(async () => {
    onChangePassword();
    try {
      const [, error] = await wrapPromise(
        changePassModalRef.current.showModal()
      );
      if (error) return;

      showLoader(CHANGE_PASSWORD_PROCESS);
      const password = await changePassword(driver.driverCode);
      setActiveDriver({ ...driver, password });
    } catch (e) {
      showMessage(UNABLE_TO_RESET_PASSWORD);
    } finally {
      hideLoader(CHANGE_PASSWORD_PROCESS);
    }
  }, [
    driver,
    onChangePassword,
    changePassModalRef,
    showLoader,
    hideLoader,
    showMessage,
  ]);

  const handleAddDriver = useCallback(async () => {
    onAddDriver();
    setCreateDriverFlow(true);
    setActiveDriver(EMPTY_DRIVER);
  }, [onAddDriver]);

  const closeSnackbar = useCallback(() => {
    setMessage("");
  }, [setMessage]);

  const updateRouteDetail = useCallback(() => {
    if (isRouteDetail) {
      dispatch(fetchRouteDetail(depotCode, routeId));
    } else {
      dispatch(fetchRoutes(depotCode, sectionId, date, uid));
    }
  }, [isRouteDetail, depotCode, routeId, sectionId, date, uid, dispatch]);

  const handleCreateNewDriver = useCallback(
    (driver) => {
      setActiveOdfDriver(EMPTY_ODF_DRIVER);
      setActiveDriver(driver);
      setCreateDriverFlow(false);
      showMessage(NEW_DRIVER_CREATED);
      updateRouteDetail();
    },
    [showMessage, updateRouteDetail]
  );

  const handleSubmitDriver = useCallback(
    async (values) => {
      handleSave();
      showLoader(SUBMIT_DRIVER_PROCESS);
      const odfRouteOwner = !isEmptyOdfDriver(odfDriver)
        ? odfDriver.driverCode
        : null;
      const driverDetails = {
        driver: { ...values, odfRouteOwner },
        depotCode,
        date,
        routeId: route.routeId,
        driverCode: values.driverCode,
      };
      try {
        if (isRouteDriverChanged(route, values)) {
          await assignRouteDriver(driverDetails);
        } else {
          await updateDriver(driverDetails);
        }
        updateRouteDetail();
        onClose();
      } catch (e) {
        const message = e?.error?.message;
        showMessage(message ? ERROR_$(message) : SOMETHING_WENT_WRONG);
      } finally {
        hideLoader(SUBMIT_DRIVER_PROCESS);
      }
    },
    [
      date,
      route,
      onClose,
      depotCode,
      handleSave,
      showLoader,
      hideLoader,
      updateRouteDetail,
      showMessage,
      odfDriver,
    ]
  );

  const updateSelectedDriver = useCallback(
    (driverCode) => {
      const selectedDriver = getDriverByCode(drivers, driverCode);
      setActiveDriver(selectedDriver);
    },
    [drivers]
  );

  const updateSelectedOdfDriver = useCallback(
    (driverCode) => {
      const selectedOdfDriver = getDriverByCode(
        drivers,
        driverCode,
        EMPTY_ODF_DRIVER
      );
      setActiveOdfDriver(selectedOdfDriver);
    },
    [drivers]
  );

  const handleSelectedOdfDriver = useCallback(
    (driverCode) => {
      if (!driverCode || driverCode === EMPTY_ODF_DRIVER.driverCode) {
        setActiveDriver(EMPTY_DRIVER);
      } else if (driver?.driverType !== DRIVER_TYPES.LINKED) {
        updateSelectedDriver(driverCode);
      }
      updateSelectedOdfDriver(driverCode);
    },
    [updateSelectedDriver, updateSelectedOdfDriver, driver]
  );

  const handleSelectDriver = useCallback(
    (driverCode) => {
      onSelectDriver(driverCode);
      updateSelectedDriver(driverCode);
    },
    [onSelectDriver, updateSelectedDriver]
  );

  const isDataReady =
    vehicles &&
    sections &&
    drivers &&
    !driversError &&
    !vehiclesError &&
    !sectionsError;

  useEffect(() => {
    if (isDataReady) {
      const routeOdfDriver = getRouteOdfDriver(drivers, route);
      updateSelectedDriver(route.driverCode);
      updateSelectedOdfDriver(routeOdfDriver);
    }
  }, [
    isDataReady,
    route,
    updateSelectedDriver,
    updateSelectedOdfDriver,
    drivers,
  ]);

  return (
    <Trackable loadId={DRIVER_DETAILS_IDS.LOAD} modal>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        transitionDuration={0}
      >
        <View className={styles.header}>
          <View className={styles.headerText}>{DRIVER_DETAILS}</View>
          <View className={styles.close}>
            <CloseIcon className={styles.icon} onClick={handleClose} />
          </View>
        </View>
        <DialogContent className={styles.container}>
          {isDataReady ? (
            <ThemeProvider theme={theme}>
              {isCreateDriverFlow ? (
                <CreateDriver
                  route={route}
                  handleCreateNewDriver={handleCreateNewDriver}
                  showMessage={showMessage}
                  vehicles={vehicles}
                  sections={sections}
                  formRef={form}
                  initialValues={EMPTY_DRIVER}
                />
              ) : (
                <DriverDetails
                  route={route}
                  handleSave={handleSubmitDriver}
                  onSelectDriver={handleSelectDriver}
                  onChangePassword={handleChangePassword}
                  onAddDriver={handleAddDriver}
                  driver={driver}
                  odfDriver={odfDriver}
                  onSelectOdfDriver={handleSelectedOdfDriver}
                  vehicles={vehicles}
                  sections={sections}
                  drivers={drivers}
                  showErrorMsg={showMessage}
                  formRef={form}
                />
              )}
            </ThemeProvider>
          ) : (
            <CircularProgress />
          )}
        </DialogContent>
        <ModalControlled
          Component={ConfirmSnackbar}
          ref={changePassModalRef}
          trackConfirm={confirmChangePassword}
          trackClose={cancelChangePassword}
        >
          {PROCEED_WITH_PASSWORD_CHANGES}
        </ModalControlled>
        <ModalControlled
          Component={ConfirmSnackbar}
          ref={saveChangesModalRef}
          messageIcon={<WarningIcon />}
          trackConfirm={confirmUnsavedChanges}
          trackClose={cancelUnsavedChanges}
        >
          {YOU_HAVE_UNSAVED_CHANGES}
          <br />
          {ARE_YOU_WANT_TO_DISCARD}
        </ModalControlled>
        <Snackbar
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          open={!!message}
          autoHideDuration={2000}
          message={message}
          onClose={closeSnackbar}
        />
      </Dialog>
    </Trackable>
  );
};

export default withTrackProps({
  cancelChangePassword: DRIVER_DETAILS_IDS.CANCEL_CHANGE_PASSWORD,
  confirmChangePassword: DRIVER_DETAILS_IDS.CONFIRM_CHANGE_PASSWORD,
  cancelUnsavedChanges: DRIVER_DETAILS_IDS.CANCEL_UNSAVED_CHANGES,
  confirmUnsavedChanges: DRIVER_DETAILS_IDS.CONFIRM_UNSAVED_CHANGES,
  onAddDriver: DRIVER_DETAILS_IDS.SELECT_ADD_NEW_DRIVER,
  onChangePassword: DRIVER_DETAILS_IDS.SELECT_CHANGE_PASSWORD,
  handleCloseModal: DRIVER_DETAILS_IDS.SELECT_CLOSE,
  onSelectDriver: (value) =>
    createLogEntryPayload(DRIVER_DETAILS_IDS.SELECT_DRIVER, { value }),
  handleSave: DRIVER_DETAILS_IDS.SELECT_SAVE_CHANGES,
})(memo(DriverDetailsDialog));
